import * as THREE from 'three';
import gsap from 'gsap';
import Experience from '../Experience.js';
import Resources from '../Utils/Resources.js';
import Data from '../Utils/Data.js';
import hairVertexShader from "../Shaders/hair/hair_vertex.glsl";
import hairFragmentShader from "../Shaders/hair/hair_fragment.glsl";

export default class Hair {

    constructor(root) {
        this.root = root;

        this.experience = Experience.getInstance();
        this.scene = this.experience.scene;
        this.time = this.experience.time;
        this.renderer = this.experience.renderer.instance;
        this.debug = this.experience.debug;
        this.sizes = this.experience.sizes;

        this.resources = Resources.getInstance();
        this.data = Data.getInstance();

        this.init();
        this.createHair();
    }

    init() {
        this.hairGeometry = null;
        this.hairMaterial = null;
        this.hairPoints = null;

        this.parameters = {
            count: 10000,
            insideColor: '#f97bce',//'#f066e9',
            outsideColor: '#c7248f',//'#89248f'//'#57155b'
        }
    }

    createHair() {
        this.hairGeometry = new THREE.BufferGeometry();

        const positions = [];
        const colors = [];

        const goldenRatio = (1 + Math.sqrt(5)) / 2; // Golden ratio for even spacing

        const layerCount = 25; //How many layers
        const minLayers = 5; // Minimum number of layers each hair should have

        const bigCircles = [
            { x: 0.2, y: 0.8, z: 0.5 },  // Top
            { x: 0.2, y: -0.8, z: 0.5 },  // Bottom
            { x: -1, y: 0.4, z: 0.3 }, // Left
        ];

        const smallCircles = [
            { x: 0.6, y: 0, z: 0.7 },  // Right
            { x: -0.5, y: -0.2, z: 0.9 },  // Left
        ];

        const baseColorIndices = [];
        const outerColorIndices = [];
        const innerColorIndices = [];

        for (let i = 0; i < this.parameters.count; i++) {
            // Small randomness factor
            const randomOffset = (Math.random() - 0.5) * 0.1; // Adjust for subtle randomness

            // Add randomness to theta, while keeping hemisphere restriction
            const theta = Math.acos(0.8 * ((i + randomOffset) / this.parameters.count) + 0.2);
            const phi = ((2 * Math.PI * i) / goldenRatio) + randomOffset; // Small phi offset

            const x = Math.sin(theta) * Math.cos(phi);
            const y = Math.sin(theta) * Math.sin(phi);
            const z = Math.cos(theta);

            // Default color is baseColor
            let selectedColor = this.data.hairColors.base;
            let colorGroup = "base"; // Track which group this hair belongs to

            for (const circle of bigCircles) {
                const dist = Math.sqrt(
                    Math.pow(x - circle.x, 2) +
                    Math.pow(y - circle.y, 2) +
                    Math.pow(z - circle.z, 2)
                );

                if (dist < 0.3) {
                    selectedColor = this.data.hairColors.inner; // Inside small circle
                    colorGroup = "inner";
                    break;
                } else if (dist < 0.5) {
                    selectedColor = this.data.hairColors.outer; // Outer ring
                    colorGroup = "outer";
                }
            }

            for (const circle of smallCircles) {
                const dist = Math.sqrt(
                    Math.pow(x - circle.x, 2) +
                    Math.pow(y - circle.y, 2) +
                    Math.pow(z - circle.z, 2)
                );

                if (dist < 0.15) {
                    selectedColor = this.data.hairColors.inner; // Inside small circle
                    colorGroup = "inner";
                    break;
                } else if (dist < 0.3) {
                    selectedColor = this.data.hairColors.outer; // Outer ring
                    colorGroup = "outer";
                }
            }

            const randomMaxLayer = Math.floor(Math.random() * (layerCount - minLayers + 1)) + minLayers;

            for (let layer = 0; layer < randomMaxLayer; layer++) {
                const fixedRadius = this.data.sphereRadius + 0.01 * layer;
                positions.push(
                    fixedRadius * Math.sin(theta) * Math.cos(phi),
                    fixedRadius * Math.sin(theta) * Math.sin(phi),
                    fixedRadius * Math.cos(theta)
                );

                //STORES COLORS
                // Store the index for easy updating
                const index = positions.length / 3 - 1;

                if (colorGroup === "base") baseColorIndices.push(index);
                else if (colorGroup === "outer") outerColorIndices.push(index);
                else if (colorGroup === "inner") innerColorIndices.push(index);

                // Apply precomputed color
                colors.push(selectedColor.r, selectedColor.g, selectedColor.b);

            }
        }

        this.baseColorIndices = baseColorIndices;
        this.outerColorIndices = outerColorIndices;
        this.innerColorIndices = innerColorIndices;

        const positionArray = new Float32Array(positions);
        const colorArrayBuffer = new Float32Array(colors);

        this.hairGeometry.setAttribute('position', new THREE.BufferAttribute(positionArray, 3));
        this.hairGeometry.setAttribute('color', new THREE.BufferAttribute(colorArrayBuffer, 3));

        //NORMALS
        if (!this.hairGeometry.hasAttribute("normal")) {
            const positions = this.hairGeometry.getAttribute("position").array;
            const normals = new Float32Array(positions.length);

            for (let i = 0; i < positions.length; i += 3) {
                let normal = new THREE.Vector3(positions[i], positions[i + 1], positions[i + 2]).normalize();
                normals[i] = normal.x;
                normals[i + 1] = normal.y;
                normals[i + 2] = normal.z;
            }

            this.hairGeometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
        }

        //

        this.hairMaterial = new THREE.ShaderMaterial({
            depthWrite: false,
            depthTest: false,
            transparent: true,
            blending: THREE.NormalBlending, // Can be changed to AdditiveBlending
            vertexColors: true,
            uniforms: {
                uTime: new THREE.Uniform(0),
                uSize: new THREE.Uniform(30 * this.renderer.getPixelRatio()),
                uResolution: { value: new THREE.Vector2(this.sizes.width, this.sizes.height) },
                uOpacity: new THREE.Uniform(1.0),
                uInfluence: new THREE.Uniform(1.5),
                uSpeed: new THREE.Uniform(0.2),
                uIntensity: new THREE.Uniform(0.3),
                uDistFromCenter: new THREE.Uniform(8.0),
                uExplosion: new THREE.Uniform(false),
                uProgress: new THREE.Uniform(0),
            },
            vertexShader: hairVertexShader,
            fragmentShader: hairFragmentShader
        });

        this.hairPoints = new THREE.Points(this.hairGeometry, this.hairMaterial);
        this.hairPoints.renderOrder = 1;
        this.root.add(this.hairPoints);

    }

    updateHairColors(newColors) {
        if (!this.hairGeometry) return;
        const colorArray = this.hairGeometry.getAttribute('color').array;

        // Update base hair color
        for (const index of this.baseColorIndices) {
            colorArray[index * 3] = newColors.base.r;
            colorArray[index * 3 + 1] = newColors.base.g;
            colorArray[index * 3 + 2] = newColors.base.b;
        }

        // Update outer ring color
        for (const index of this.outerColorIndices) {
            colorArray[index * 3] = newColors.outer.r;
            colorArray[index * 3 + 1] = newColors.outer.g;
            colorArray[index * 3 + 2] = newColors.outer.b;
        }

        // Update inner circle color
        for (const index of this.innerColorIndices) {
            colorArray[index * 3] = newColors.inner.r;
            colorArray[index * 3 + 1] = newColors.inner.g;
            colorArray[index * 3 + 2] = newColors.inner.b;
        }

        // Mark colors for update
        this.hairGeometry.getAttribute('color').needsUpdate = true;
    }

    updateHairAlpha(alpha) {
        this.hairMaterial.uniforms.uOpacity.value = alpha;
    }

    updateHairSpeed(speed, intensity, distance)
    {
        this.hairMaterial.uniforms.uSpeed.value = speed;
        this.hairMaterial.uniforms.uIntensity.value = intensity;
        this.hairMaterial.uniforms.uDistFromCenter.value = distance;
    }

    explode()
    {
        this.hairMaterial.uniforms.uExplosion.value = true;
        gsap.to(
            this.hairMaterial.uniforms.uProgress,
            { value: 1, ease: 'power2', duration: 4},
        )
    }

    reset()
    {
        this.hairMaterial.uniforms.uExplosion.value = false;
        this.hairMaterial.uniforms.uProgress.value = 0;
    }

    update() {
        this.hairMaterial.uniforms.uTime.value = this.time.elapsed * 0.001; // Smooth animation

        // this.hairMaterial.opacity = Math.max(0.0, 1.0 - this.time.elapsed * 0.01);

    }

    resize() {
        this.hairMaterial.uniforms.uResolution.value = new THREE.Vector2(this.sizes.width, this.sizes.height);
    }
}